import { Platform } from 'react-native';
import AppManifest from './AppManifest';
import { log } from '../tools/LogHelper';

const DSN =
  'https://a20df13274948d23050373931765388a@o4509810594545664.ingest.us.sentry.io/4509833321054208';

const env = AppManifest.env || 'development';

// Guard the @sentry/react-native import: older native binaries shipped before
// the SDK was linked will throw at module evaluation if we use a static import,
// which (under React 19's strict lazy resolver) becomes a fatal crash.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Sentry: any;
try {
  Sentry = require('@sentry/react-native');
} catch (error) {
  log('Sentry native module unavailable; instrumentation disabled', String(error));
  Sentry = null;
}

export const routingInstrumentation = Sentry
  ? Sentry.reactNavigationIntegration()
  : undefined;

if (Sentry && env !== 'development') {
  try {
    Sentry.init({
      dsn: DSN,
      environment: env,
      release: `chloe-app@${AppManifest.version}+${AppManifest.buildNumber}`,
      dist: String(AppManifest.buildNumber),
      enabled: true,
      // Always trace wearable sync so sync issues are always captured; baseline elsewhere.
      tracesSampler: (samplingContext: any) => {
        // Sync transactions are always captured, even within an unsampled parent trace.
        const name = String(samplingContext?.name || '');
        if (name.startsWith('wearable.sync')) return 1.0;
        if (typeof samplingContext?.parentSampled === 'boolean') {
          return samplingContext.parentSampled;
        }
        return env === 'production' ? 0.2 : 1.0;
      },
      sendDefaultPii: false,
      integrations: [
        routingInstrumentation,
        Sentry.breadcrumbsIntegration({
          console: true,
          fetch: true,
          xhr: true
        })
      ],
      enableAutoSessionTracking: true,
      attachStacktrace: true,
      beforeBreadcrumb(breadcrumb: any) {
        if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
          const url = breadcrumb.data?.url || '';
          if (typeof url === 'string' && url.includes('/api/')) {
            return breadcrumb;
          }
          return null;
        }
        return breadcrumb;
      },
      beforeSend(event: any) {
        if (Platform.OS === 'web') {
          return null;
        }
        return event;
      }
    });
  } catch (error) {
    log('Sentry.init failed', String(error));
  }
}

export function setSentryUser(user: { id: number }) {
  if (!Sentry) return;
  try {
    Sentry.setUser({ id: String(user.id) });
  } catch (error) {
    log('Sentry.setUser failed', String(error));
  }
}

export function clearSentryUser() {
  if (!Sentry) return;
  try {
    Sentry.setUser(null);
  } catch (error) {
    log('Sentry.setUser failed', String(error));
  }
}

// --- Wearable-sync performance instrumentation (all null-safe / SDK-version-safe) ---

// Span attributes are OpenTelemetry-backed: only primitives are allowed.
type SyncAttributes = Record<string, string | number | boolean>;

// Wrap a full sync run in a Sentry transaction. No-ops if the native SDK is absent.
export async function withSyncSpan<T>(
  name: string,
  attributes: SyncAttributes,
  fn: () => Promise<T>
): Promise<T> {
  if (!Sentry || typeof Sentry.startSpan !== 'function') return fn();
  return Sentry.startSpan(
    { name, op: 'wearable.sync', forceTransaction: true, attributes },
    fn
  );
}

// Wrap a sub-step (fetch, upload, per-type read) as a child span of the active sync span.
export async function withSyncChildSpan<T>(
  name: string,
  op: string,
  attributes: SyncAttributes,
  fn: () => Promise<T>
): Promise<T> {
  if (!Sentry || typeof Sentry.startSpan !== 'function') return fn();
  return Sentry.startSpan({ name, op, attributes }, fn);
}

// Attach a measurement/attribute to the currently active span.
export function setSyncAttribute(key: string, value: number | string) {
  if (!Sentry || typeof Sentry.getActiveSpan !== 'function') return;
  try {
    Sentry.getActiveSpan()?.setAttribute?.(key, value);
  } catch {
    // ignore
  }
}

// Report a sync error to Sentry, tagged for filtering. Marks the error so an outer
// catch can skip re-reporting the same failure (avoids duplicate events).
export function captureSyncException(
  error: unknown,
  context?: Record<string, string>
) {
  if (!Sentry) return;
  // Only mark as captured once we actually have an SDK to send it.
  if (error && typeof error === 'object') {
    (error as { __sentryCaptured?: boolean }).__sentryCaptured = true;
  }
  try {
    Sentry.captureException(error, {
      tags: { component: 'wearable-sync', ...(context || {}) }
    });
  } catch (e) {
    log('Sentry.captureException failed', String(e));
  }
}

/**
 * GPS capture faults, tagged separately from wearable-sync so the two can be
 * filtered and alerted on independently.
 *
 * The GPS path had no instrumentation at all: every failure went through
 * log(), which is console.log on a device nobody has attached, and two of the
 * paths that matter most — getCurrentPosition and sync, the calls that
 * actually produce and deliver data — were swallowed with
 * `.catch(() => undefined)` and did not even log. An outage was therefore
 * undiagnosable after the fact.
 */
export function captureGpsException(
  error: unknown,
  context?: Record<string, string>
) {
  if (!Sentry) return;
  try {
    Sentry.captureException(error, {
      // component last: a caller passing context.component would otherwise
      // overwrite the tag every GPS filter and alert keys off
      tags: { ...(context || {}), component: 'gps' }
    });
  } catch (e) {
    log('Sentry.captureException failed', String(e));
  }
}

/**
 * Lifecycle trail for the GPS path. Breadcrumbs are only transmitted when an
 * event is actually sent, so the timeline leading up to a fault rides along
 * with it for free rather than costing events of its own — which matters on a
 * quota that has already been exhausted once.
 */
export function gpsBreadcrumb(message: string, data?: Record<string, unknown>) {
  if (!Sentry || typeof Sentry.addBreadcrumb !== 'function') return;
  try {
    Sentry.addBreadcrumb({ category: 'gps', level: 'info', message, data });
  } catch {
    // instrumentation must never break capture
  }
}

// True if this error was already reported via captureSyncException.
export function isSyncExceptionCaptured(error: unknown): boolean {
  return !!(
    error &&
    typeof error === 'object' &&
    (error as { __sentryCaptured?: boolean }).__sentryCaptured
  );
}

export default Sentry;
